import { CalendarDays } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { brToIso, isoToBr, maskDate } from "../lib/format";

interface DateInputProps {
  /** Data no formato AAAA-MM-DD (vazio quando não preenchida ou incompleta). */
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  ariaLabel?: string;
}

/**
 * Campo de data que sempre exibe e aceita dia/mês/ano, independentemente do idioma do navegador
 * (o `<input type="date">` nativo segue a configuração do sistema). O valor trocado com a tela continua AAAA-MM-DD.
 */
export function DateInput({ value, onChange, id, required, disabled, min, max, ariaLabel }: DateInputProps) {
  const [text, setText] = useState(isoToBr(value));
  const textRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLInputElement>(null);

  // Mudança vinda de fora (carregar registro, limpar formulário). Digitação incompleta mantém o texto.
  useEffect(() => { setText((current) => (brToIso(current) === (value || "") ? current : isoToBr(value))); }, [value]);

  useEffect(() => {
    textRef.current?.setCustomValidity(text && !brToIso(text) ? "Informe uma data válida no formato dd/mm/aaaa." : "");
  }, [text]);

  const update = (raw: string) => {
    const masked = maskDate(raw);
    setText(masked);
    const iso = brToIso(masked);
    if (iso !== (value || "")) onChange(iso);
  };
  const openPicker = () => {
    const picker = pickerRef.current;
    if (!picker) return;
    try { picker.showPicker(); } catch { picker.focus(); }
  };

  return <span className="date-input">
    <input ref={textRef} id={id} type="text" inputMode="numeric" autoComplete="off" placeholder="dd/mm/aaaa" maxLength={10} required={required} disabled={disabled} aria-label={ariaLabel} value={text} onChange={(event) => update(event.target.value)}/>
    <button type="button" className="date-input-button" onClick={openPicker} disabled={disabled} aria-label="Abrir calendário"><CalendarDays size={16} aria-hidden="true"/></button>
    <input ref={pickerRef} className="date-input-native" type="date" tabIndex={-1} aria-hidden="true" value={value || ""} min={min} max={max} disabled={disabled} onChange={(event) => { if (event.target.value) update(isoToBr(event.target.value)); }}/>
  </span>;
}
